import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BookOpen, Plus, Trash2, Search, Play, Activity, LayoutDashboard, Zap } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative, fmtDuration } from "@/lib/format";
import RichTextEditor from "@/components/RichTextEditor";
import SOPStepEditor from "@/components/sops/SOPStepEditor";
import SOPBottlenecksPanel from "@/components/sops/SOPBottlenecksPanel";
import SOPPreview from "@/components/sops/SOPPreview";
import SOPIntelligencePanel from "@/components/sops/SOPIntelligencePanel";
import { useSopRunner } from "@/components/sops/SopRunnerContext";

export default function SOPs() {
  const { activeWorkspaceId, activeWorkspace, user, memberships } = useAuth();
  const { can } = useWorkspacePerms();
  const _membership = memberships?.find((m) => m.workspace_id === activeWorkspaceId);
  const isAdmin = ["owner", "admin", "workspace_owner", "workspace_admin"].includes(_membership?.role)
    || ["owner", "admin"].includes(activeWorkspace?.role)
    || activeWorkspace?.owner_id === user?.id
    || can("sop.manage");
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [q, setQ] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterScope, setFilterScope] = useState("mine"); // 'mine' = workspace + my departments; 'all' = everything
  const [filterTag, setFilterTag] = useState("");
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [tab, setTab] = useState("library");
  const { startRun: ctxStartRun, openRun: setActiveRunId } = useSopRunner();

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filterDept) params.set("department_id", filterDept);
      if (filterCat) params.set("category", filterCat);
      if (filterTag) params.set("tag", filterTag);
      if (filterScope === "mine") params.set("scope", "mine");
      if (q) params.set("q", q);
      const [s, d] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/sops?${params}`),
        api.get(`/workspaces/${activeWorkspaceId}/departments`).catch(() => ({ data: [] })),
      ]);
      setRows(s.data || []);
      setDepartments(d.data || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId, filterDept, filterCat, filterScope, filterTag]);

  const categories = useMemo(() => Array.from(new Set(rows.map((r) => r.category).filter(Boolean))).sort(), [rows]);
  const allTags = useMemo(() => Array.from(new Set(rows.flatMap((r) => r.tags || []))).sort(), [rows]);
  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) => (r.title || "").toLowerCase().includes(s) || (r.body || "").toLowerCase().includes(s) || (r.category || "").toLowerCase().includes(s));
  }, [rows, q]);

  const del = async (sop) => {
    if (!confirm(`Delete SOP "${sop.title}"?`)) return;
    try { await api.delete(`/sops/${sop.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const startRun = async (sop) => {
    await ctxStartRun(sop);
  };

  return (
    <>
      <PageHeader
        eyebrow="Knowledge"
        title="Standard Operating Procedures"
        description="Department- and category-scoped SOPs. Build steps, run playbooks, and track completion."
        testid="sops-header"
        actions={isAdmin && <Button className="rounded-none" onClick={() => setEditing({})} data-testid="sops-new-btn"><Plus className="w-4 h-4 mr-2" /> New SOP</Button>}
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
            <TabsTrigger value="library" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="sops-tab-library"><BookOpen className="w-4 h-4 mr-2" />Library</TabsTrigger>
            <TabsTrigger value="runs" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="sops-tab-runs"><Activity className="w-4 h-4 mr-2" />Runs</TabsTrigger>
            <TabsTrigger value="dashboard" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="sops-tab-dashboard"><LayoutDashboard className="w-4 h-4 mr-2" />Dashboard</TabsTrigger>
            <TabsTrigger value="intelligence" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="sops-tab-intelligence"><Zap className="w-4 h-4 mr-2" />Intelligence</TabsTrigger>
          </TabsList>
          <TabsContent value="library" className="mt-4">
            <div className="border border-border bg-muted/40 p-3 flex items-center gap-2 flex-wrap" data-testid="sops-filters">
              <Search className="w-3.5 h-3.5" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, body, tag, category…" className="h-8 w-72 text-xs" data-testid="sops-q" />
              <Select value={filterScope} onValueChange={setFilterScope}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="sops-filter-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mine">Mine + workspace-wide</SelectItem>
                  <SelectItem value="all">Everything</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterDept || "_all"} onValueChange={(v) => setFilterDept(v === "_all" ? "" : v)}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="sops-filter-dept"><SelectValue placeholder="All departments" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All departments</SelectItem>
                  {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {categories.length > 0 && (
                <Select value={filterCat || "_all"} onValueChange={(v) => setFilterCat(v === "_all" ? "" : v)}>
                  <SelectTrigger className="h-8 w-44 text-xs" data-testid="sops-filter-cat"><SelectValue placeholder="All categories" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All categories</SelectItem>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Select value={filterTag || "_all"} onValueChange={(v) => setFilterTag(v === "_all" ? "" : v)}>
                <SelectTrigger className="h-8 w-40 text-xs" data-testid="sops-filter-tag"><SelectValue placeholder="All tags" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All tags</SelectItem>
                  {allTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-[11px] ml-auto">{filtered.length} of {rows.length}</span>
            </div>
            <div className="py-6">
              {busy && rows.length === 0 ? <div className="text-sm">Loading…</div>
                : filtered.length === 0 ? (
                  <EmptyState
                    title="No SOPs yet"
                    description={isAdmin ? "Document onboarding, escalation paths, vendor processes, design QA — anything that needs to be repeatable." : "Your admin team hasn't added any SOPs yet."}
                    icon={BookOpen}
                    testid="sops-empty"
                    action={isAdmin && <Button className="rounded-none" onClick={() => setEditing({})} data-testid="sops-empty-cta">Add first SOP</Button>}
                  />
                ) : (
                  <div className="border border-border" data-testid="sops-table">
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/30 text-[10px] uppercase tracking-widest text-foreground/55 font-semibold">
                      <div className="col-span-4">Title</div>
                      <div className="col-span-2">Category · Version</div>
                      <div className="col-span-2">Departments</div>
                      <div className="col-span-1">Tags</div>
                      <div className="col-span-1">Updated</div>
                      <div className="col-span-2 text-right">Actions</div>
                    </div>
                    {filtered.map((sop) => (
                      <div key={sop.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-border items-center text-sm hover:bg-muted/30 cursor-pointer" data-testid={`sop-row-${sop.id}`} onClick={(e) => { if (!e.target.closest("button")) setViewing(sop); }}>
                        <div className="col-span-4 min-w-0">
                          <div className="font-semibold truncate">{sop.title}</div>
                          <div className="text-[11px] text-meta truncate">{(sop.body || "").replace(/<[^>]+>/g, "").slice(0, 80)}</div>
                        </div>
                        <div className="col-span-2 text-xs">
                          {sop.category && <span className="chip text-[10px] mr-1">{sop.category}</span>}
                          <span className="chip text-[10px] chip-internal">{sop.version || "v1"}</span>
                        </div>
                        <div className="col-span-2 flex flex-wrap gap-1">
                          {(sop.department_ids || []).length === 0 ? <span className="chip text-[10px]">All</span> : (sop.department_ids || []).slice(0, 3).map((did) => {
                            const d = departments.find((x) => x.id === did);
                            return d ? <span key={did} className="chip text-[10px]">{d.name}</span> : null;
                          })}
                          {(sop.department_ids || []).length > 3 && <span className="text-[10px] text-meta">+{(sop.department_ids || []).length - 3}</span>}
                        </div>
                        <div className="col-span-1 flex flex-wrap gap-1">
                          {(sop.tags || []).slice(0, 2).map((t) => (
                            <span key={t} className="chip text-[10px]" data-testid={`sop-tag-${sop.id}-${t}`}>#{t}</span>
                          ))}
                          {(sop.tags || []).length > 2 && <span className="text-[10px] text-meta">+{(sop.tags || []).length - 2}</span>}
                        </div>
                        <div className="col-span-1 text-[10px] text-meta">{fmtRelative(sop.updated_at)}</div>
                        <div className="col-span-2 text-right flex gap-1 justify-end">
                          <Button size="sm" className="rounded-none h-7 px-2 bg-primary hover:bg-primary/90" onClick={() => startRun(sop)} data-testid={`sop-run-${sop.id}`}><Play className="w-3 h-3 mr-1" /> Run</Button>
                          {isAdmin && <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => setEditing(sop)} data-testid={`sop-edit-${sop.id}`}>Edit</Button>}
                          {isAdmin && <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => del(sop)} data-testid={`sop-delete-${sop.id}`}><Trash2 className="w-3 h-3" /></Button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </TabsContent>
          <TabsContent value="runs" className="mt-4">
            <RunsTab wsId={activeWorkspaceId} onOpen={setActiveRunId} />
          </TabsContent>
          <TabsContent value="dashboard" className="mt-4">
            <SOPDashboard wsId={activeWorkspaceId} onOpen={setActiveRunId} />
          </TabsContent>
          <TabsContent value="intelligence" className="mt-4">
            <SOPIntelligencePanel wsId={activeWorkspaceId} />
          </TabsContent>
        </Tabs>
      </div>

      {editing && <SOPDialog wsId={activeWorkspaceId} sop={editing} departments={departments} onClose={() => setEditing(null)} onSaved={async () => { await load(); setEditing(null); }} />}
      {viewing && <SOPPreview sop={viewing} departments={departments} onClose={() => setViewing(null)} onRun={() => { setViewing(null); startRun(viewing); }} />}
    </>
  );
}

function SOPDialog({ wsId, sop, departments, onClose, onSaved }) {
  const isNew = !sop?.id;
  const [form, setForm] = useState({
    title: sop.title || "",
    category: sop.category || "",
    department_ids: sop.department_ids || [],
    tags: sop.tags || [],
    body: sop.body || "",
    version: sop.version || "v1",
    visibility: sop.visibility || "workspace",
    requires_ack: !!sop.requires_ack,
    change_summary: "",
  });
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleDept = (id) => setForm((s) => ({ ...s, department_ids: s.department_ids.includes(id) ? s.department_ids.filter((x) => x !== id) : [...s.department_ids, id] }));

  const save = async () => {
    if (!form.title?.trim()) { toast.error("Title is required"); return; }
    // Feb 2026 · Body is now optional at create-time — backend R148e
    // accepts an empty body so users can save a skeleton SOP first and
    // add steps / body afterwards. On EDIT we still require a body so
    // the user doesn't accidentally wipe an existing document.
    if (!isNew && !form.body?.trim()) { toast.error("Body is required for existing SOPs"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (isNew) {
        delete payload.change_summary;
        await api.post(`/workspaces/${wsId}/sops`, payload);
      } else {
        // If user didn't change version explicitly, drop it so backend can auto-bump on body change
        if (payload.version === (sop.version || "v1")) {
          delete payload.version;
        }
        await api.patch(`/sops/${sop.id}`, payload);
      }
      toast.success("Saved");
      await onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-auto" data-testid="sop-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New SOP" : `Edit · ${sop.title}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Title *" value={form.title} onChange={(v) => set("title", v)} testid="sop-title" />
          <div className="grid grid-cols-4 gap-3">
            <Field label="Category" value={form.category} onChange={(v) => set("category", v)} testid="sop-category" />
            <Field label="Version" value={form.version} onChange={(v) => set("version", v)} testid="sop-version" />
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Visibility</Label>
              <Select value={form.visibility} onValueChange={(v) => set("visibility", v)}>
                <SelectTrigger className="h-10" data-testid="sop-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="workspace">Whole workspace</SelectItem>
                  <SelectItem value="department">Department-only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="inline-flex items-end gap-2 text-sm pb-2">
              <input type="checkbox" checked={form.requires_ack} onChange={(e) => set("requires_ack", e.target.checked)} data-testid="sop-requires-ack" />
              Requires read-confirmation
            </label>
          </div>
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Applies to departments</Label>
            <div className="flex flex-wrap gap-1" data-testid="sop-departments">
              {departments.length === 0 ? <div className="text-xs text-muted-foreground">No departments yet — create some under HR → Departments first.</div>
                : departments.map((d) => (
                  <button key={d.id} type="button" onClick={() => toggleDept(d.id)} className={`chip text-[10px] ${form.department_ids.includes(d.id) ? "chip-approved" : ""}`} data-testid={`sop-dept-${d.id}`}>
                    {form.department_ids.includes(d.id) ? "✓ " : ""}{d.name}
                  </button>
                ))}
            </div>
            <p className="text-[10px] text-meta mt-1">Leave empty to make this SOP visible to <strong>everyone</strong> in the workspace.</p>
          </div>
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Tags</Label>
            <div className="flex items-center gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Type a tag and press Enter"
                className="h-9 rounded-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && tagInput.trim()) {
                    e.preventDefault();
                    const t = tagInput.trim().toLowerCase().replace(/^#/, "");
                    if (!form.tags.includes(t)) set("tags", [...form.tags, t]);
                    setTagInput("");
                  }
                }}
                data-testid="sop-tags-input"
              />
              <Button type="button" variant="outline" className="rounded-none h-9" onClick={() => {
                const t = tagInput.trim().toLowerCase().replace(/^#/, "");
                if (t && !form.tags.includes(t)) set("tags", [...form.tags, t]);
                setTagInput("");
              }} data-testid="sop-tags-add">Add</Button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2" data-testid="sop-tags-list">
              {form.tags.length === 0 ? <span className="text-[11px] text-meta">No tags yet — add a few to help people find this SOP faster.</span>
                : form.tags.map((t) => (
                <span key={t} className="chip text-[10px] flex items-center gap-1" data-testid={`sop-tag-chip-${t}`}>
                  #{t}
                  <button type="button" className="text-foreground/60 hover:text-destructive" onClick={() => set("tags", form.tags.filter((x) => x !== t))} data-testid={`sop-tag-remove-${t}`}>×</button>
                </span>
              ))}
            </div>
          </div>
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Body{isNew ? " (optional — you can save a skeleton and fill in later)" : " *"} (rich text — supports images, YouTube embeds, lists)</Label>
            <RichTextEditor value={form.body} onChange={(html) => set("body", html)} placeholder="Document your process — steps, screenshots, embedded videos…" minHeight={280} workspaceId={wsId} testid="sop-body" />
          </div>
          {!isNew && (
            <Field label="Change summary (for version history)" value={form.change_summary} onChange={(v) => set("change_summary", v)} testid="sop-change-summary" />
          )}
          {!isNew && (
            <div className="border-t border-border pt-3">
              <SOPStepEditor sopId={sop.id} sopTitle={form.title} />
            </div>
          )}
          {isNew && (
            <div className="text-xs text-muted-foreground italic border-t border-border pt-3">
              Save this SOP first, then re-open it to add ordered steps (checkbox / form / upload / video / navigate / embed).
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="sop-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={testid} />
    </div>
  );
}

// ─────────────────── Runs Tab ───────────────────

function RunsTab({ wsId, onOpen }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all"); // all | mine | active | completed
  const [busy, setBusy] = useState(true);
  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filter === "mine") params.set("mine", "true");
      else if (filter === "active") params.set("status", "in_progress");
      else if (filter === "completed") params.set("status", "completed");
      const { data } = await api.get(`/workspaces/${wsId}/sop-runs?${params}`);
      setRows(data || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { if (wsId) load(); }, [wsId, filter]);   // eslint-disable-line

  return (
    <div className="space-y-3" data-testid="sops-runs-tab">
      <div className="flex items-center gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-8 w-40 rounded-none text-xs" data-testid="sops-runs-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All runs</SelectItem>
            <SelectItem value="mine">Assigned to me</SelectItem>
            <SelectItem value="active">In progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[11px] text-muted-foreground">{rows.length} runs</span>
      </div>
      {busy ? <div className="text-sm">Loading…</div>
        : rows.length === 0 ? <EmptyState title="No runs yet" description="Click Run on any SOP to start an execution." icon={Activity} testid="sops-runs-empty" />
        : (
          <div className="border border-border" data-testid="sops-runs-list">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/30 text-[10px] uppercase tracking-widest text-foreground/55 font-semibold">
              <div className="col-span-3">SOP · version</div>
              <div className="col-span-2">Assignee</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Started</div>
              <div className="col-span-1 text-right">Duration</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {rows.map((r) => (
              <div key={r.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-border items-center text-sm" data-testid={`sop-run-row-${r.id}`}>
                <div className="col-span-3"><b>{r.sop_title}</b> <span className="chip text-[10px] chip-internal">{r.sop_version || "v1"}</span></div>
                <div className="col-span-2 text-xs truncate">{r.assignee_id?.slice(0, 8)}…</div>
                <div className="col-span-2"><span className={`chip text-[10px] ${r.status === "completed" ? "chip-approved" : r.status === "cancelled" ? "chip-rejected" : "chip-pending"}`}>{r.status}</span></div>
                <div className="col-span-2 text-[11px] text-meta">{fmtRelative(r.started_at)}</div>
                <div className="col-span-1 text-right font-mono text-[11px]" data-testid={`sop-run-duration-${r.id}`}>
                  {r.status === "completed" && r.started_at && r.completed_at
                    ? fmtDuration((new Date(r.completed_at) - new Date(r.started_at)) / 1000)
                    : "—"}
                </div>
                <div className="col-span-2 text-right">
                  <Button size="sm" className="rounded-none h-7" onClick={() => onOpen(r.id)} data-testid={`sop-run-open-${r.id}`}>
                    {r.status === "in_progress" ? "Resume" : "View"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ─────────────────── Dashboard Tab ───────────────────

function SOPDashboard({ wsId, onOpen }) {
  const [d, setD] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get(`/workspaces/${wsId}/sop-dashboard`);
      setD(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load dashboard")); }
  };
  useEffect(() => { if (wsId) load(); }, [wsId]);   // eslint-disable-line

  if (!d) return <div className="text-sm">Loading…</div>;
  const c = d.counts || {};
  return (
    <div className="space-y-5" data-testid="sops-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="My active runs" value={c.my_active} testid="sop-stat-my-active" />
        <Stat label="Workspace active" value={c.ws_active} testid="sop-stat-ws-active" />
        <Stat label="Completed (30d)" value={c.ws_completed_30d} testid="sop-stat-completed" />
        <Stat label="My completions (30d)" value={c.my_completed_30d} testid="sop-stat-my-completed" />
        <Stat label="Overdue" value={c.overdue} testid="sop-stat-overdue" tone={c.overdue ? "danger" : "ok"} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="border border-border">
          <div className="px-3 py-2 label-eyebrow text-[10px] bg-muted/30 border-b border-border">Top SOPs (last 90d)</div>
          {(d.top_sops || []).length === 0 ? <div className="p-4 text-xs text-muted-foreground">No runs yet.</div>
            : (d.top_sops || []).map((t) => (
              <div key={t.sop_id} className="flex items-center gap-2 px-3 py-2 border-b border-border text-sm" data-testid={`sop-top-${t.sop_id}`}>
                <span className="truncate flex-1">{t.sop_title}</span>
                <span className="font-mono text-xs">{t.runs} runs</span>
                <span className="font-mono text-xs text-emerald-600">{t.completion_rate}%</span>
              </div>
            ))}
        </div>
        <div className="border border-border">
          <div className="px-3 py-2 label-eyebrow text-[10px] bg-muted/30 border-b border-border">My active runs</div>
          {(d.my_active_runs || []).length === 0 ? <div className="p-4 text-xs text-muted-foreground">Nothing on your plate. 🎉</div>
            : (d.my_active_runs || []).map((r) => (
              <div key={r.id} className="flex items-center gap-2 px-3 py-2 border-b border-border text-sm" data-testid={`sop-my-run-${r.id}`}>
                <b className="flex-1 truncate">{r.sop_title}</b>
                <span className="text-[11px] text-meta">{fmtRelative(r.started_at)}</span>
                <Button size="sm" className="rounded-none h-7" onClick={() => onOpen(r.id)} data-testid={`sop-my-run-resume-${r.id}`}>
                  Resume
                </Button>
              </div>
            ))}
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <SOPBottlenecksPanel wsId={wsId} />
      </div>
    </div>
  );
}

function Stat({ label, value, testid, tone = "ok" }) {
  const c = tone === "danger" ? "text-red-600" : tone === "warn" ? "text-amber-600" : "";
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className={`text-2xl font-black font-mono ${c}`}>{value ?? 0}</div>
    </div>
  );
}
